import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { QrCode, Loader2 } from 'lucide-react'
import { useFormularioConfig, useCanjearCodigo } from '../../hooks/useQrCodigo'
import { DynamicForm } from '../../components/DynamicForm'

export function FormularioPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const token = (location.state as { token?: string })?.token || ''

  const { data: config, isLoading } = useFormularioConfig(uuid)
  const canjearMutation = useCanjearCodigo()

  const handleSubmit = async (datos: Record<string, string>) => {
    if (!uuid || !token) return

    try {
      const result = await canjearMutation.mutateAsync({ uuid, token, datos })
      if (result.success && result.download_token) {
        navigate(`/qr/descarga/${encodeURIComponent(result.download_token)}`)
      }
    } catch {
      // Error handled by mutation state
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Sesión expirada. Valide su código nuevamente.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-950">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-6 px-4">
        <div className="max-w-lg mx-auto text-center">
          <QrCode className="h-10 w-10 mx-auto mb-2 opacity-80" />
          <h1 className="text-xl font-bold">{config?.libro_nombre || 'Formulario'}</h1>
          {config?.libro_descripcion && (
            <p className="text-blue-100 mt-1 text-sm">{config.libro_descripcion}</p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4">
        {config?.imagen_portada_url && (
          <img
            src={config.imagen_portada_url}
            alt={config.libro_nombre}
            className="w-full rounded-t-lg object-cover max-h-48"
          />
        )}

        <Card className="border-0 shadow-lg rounded-t-none">
          <CardHeader>
            <CardTitle className="text-lg">Complete el formulario</CardTitle>
            <p className="text-sm text-muted-foreground">
              Complete los datos para acceder a la descarga
            </p>
          </CardHeader>
          <CardContent>
            {config?.campos && (
              <DynamicForm
                campos={config.campos}
                onSubmit={handleSubmit}
                isSubmitting={canjearMutation.isPending}
                submitLabel="Descargar"
              />
            )}
            {canjearMutation.isError && (
              <p className="text-sm text-red-500 mt-3 text-center">
                Error al procesar. Intente nuevamente.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
